import React, { useEffect, useState } from 'react';
import { Mail, AlertCircle } from 'lucide-react';
import { Dialog } from '../ui/Dialog.js';
import { Button } from '../ui/Button.js';
import { Field, Input } from '../ui/Field.js';
import { useToast } from '../ui/index.js';
import { apiClient, ApiError } from '../../utils/apiClient.js';
import { useAuthStore } from '../../stores/authStore.js';

/** FRE-137: change the account email at any time. The code goes to the new address; the email switches when it is entered. */
export const ChangeEmailDialog: React.FC = () => {
  const toast = useToast();
  const open = useAuthStore((s) => s.changeEmailDialogOpen);
  const currentEmail = useAuthStore((s) => s.user?.email);
  const close = useAuthStore((s) => s.closeChangeEmailDialog);

  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setNewEmail('');
      setPassword('');
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const submit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (busy) return;
    const email = newEmail.trim();
    if (!email || !password) {
      setError('ایمیل جدید و رمز عبور را وارد کنید.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await apiClient.post<{ pending_email: string; emailSent: boolean }>('/api/auth/change-email', {
        new_email: email,
        password
      });
      const store = useAuthStore.getState();
      store.setPendingEmail(res.pending_email);
      store.closeChangeEmailDialog();
      store.openVerifyDialog();
      if (res.emailSent) toast.success('کد تایید به ایمیل جدید ارسال شد.');
      else toast.info('ارسال ایمیل انجام نشد؛ در پنجره بعدی «ارسال دوباره» را بزنید.');
    } catch (err: unknown) {
      if (err instanceof ApiError && err.data.code === 'WRONG_PASSWORD') {
        setError('رمز عبور اشتباه است.');
      } else if (err instanceof ApiError && err.data.code === 'EMAIL_TAKEN') {
        setError('این ایمیل قبلاً در فرت بورد ثبت شده است.');
      } else if (err instanceof ApiError && err.data.code === 'SAME_EMAIL') {
        setError('این همان ایمیل فعلی شماست.');
      } else if (err instanceof ApiError && err.status === 400) {
        setError('ایمیل واردشده معتبر نیست.');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('کمی صبر کنید و دوباره تلاش کنید.');
      } else {
        setError('تغییر ایمیل انجام نشد؛ اتصال اینترنت را بررسی کنید.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      title="تغییر ایمیل"
      titleHeadingLevel="h3"
      description={currentEmail ? `ایمیل فعلی: ${currentEmail}. کد تایید به ایمیل جدید ارسال می‌شود و بعد از وارد کردن آن، ایمیل حساب شما عوض می‌شود.` : undefined}
      size="sm"
      data-testid="change-email-dialog"
      footer={
        <>
          <Button variant="primary" onClick={() => void submit()} loading={busy} leadingIcon={<Mail className="size-4" />} data-testid="change-email-submit">
            ارسال کد
          </Button>
          <Button variant="ghost" onClick={close} disabled={busy} data-testid="change-email-cancel">
            انصراف
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-3.5">
        {error && (
          <div role="alert" className="flex items-center gap-2 p-3 text-xs rounded-xl bg-error/15 border border-error/30 text-error-soft">
            <AlertCircle className="size-4 shrink-0" />
            <span data-testid="change-email-error">{error}</span>
          </div>
        )}
        <Field label="ایمیل جدید">
          <Input
            type="email"
            dir="ltr"
            isLatin
            autoComplete="email"
            placeholder="name@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            data-testid="change-email-input"
          />
        </Field>
        <Field label="رمز عبور (برای تایید هویت)">
          <Input
            type="password"
            dir="ltr"
            isLatin
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="change-email-password"
          />
        </Field>
        <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
};
